import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { ChevronLeft, ChevronRight, X, Plus, Trash2 } from "lucide-react";
import { useConfirm } from "@/components/ConfirmDialog";

const TAG_COLOR = {
    Hypertrophy: "#FF4D00",
    Performance: "#CCFF00",
    Rehab: "#FFD600",
    Strength: "#FF6A22",
    Mobility: "#888888",
    general: "#888888",
};

function ymd(d) {
    return d.toISOString().slice(0, 10);
}
function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
function addDays(d, n) { return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n); }

function buildMonthGrid(anchor) {
    const first = startOfMonth(anchor);
    const last = endOfMonth(anchor);
    const startWeekday = (first.getDay() + 6) % 7; // Monday-first
    const start = addDays(first, -startWeekday);
    const days = [];
    let cur = start;
    while (cur <= last || days.length % 7 !== 0) {
        days.push(cur);
        cur = addDays(cur, 1);
    }
    return days;
}

function AssignModal({ open, onClose, date, clients, onSaved, editing }) {
    const [form, setForm] = useState({
        client_id: "", title: "", type: "workout", date: "", time: "", duration_min: 60, description: "", completed: false,
    });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const confirm = useConfirm();

    useEffect(() => {
        if (editing) setForm({ ...editing });
        else setForm({
            client_id: clients[0]?.id || "", title: "", type: "workout",
            date: date ? ymd(date) : ymd(new Date()),
            time: "07:00", duration_min: 60, description: "", completed: false,
        });
        setErr("");
    }, [open, editing, date, clients]);

    if (!open) return null;

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true); setErr("");
        try {
            if (editing?.id) {
                const { data } = await api.put(`/workouts/${editing.id}`, form);
                onSaved(data, "update");
            } else {
                const { data } = await api.post("/workouts", form);
                onSaved(data, "create");
            }
            onClose();
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || e.message);
        } finally { setBusy(false); }
    };

    const remove = async () => {
        if (!editing?.id) return;
        const ok = await confirm({ title: "Delete workout?", message: `"${editing.title || "This workout"}" will be removed from the calendar.`, confirmLabel: "Delete workout" });
        if (!ok) return;
        await api.delete(`/workouts/${editing.id}`);
        onSaved(editing, "delete");
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="assign-modal">
            <div className="bg-[var(--surface)] border border-[var(--border-bright)] w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between p-5 border-b border-[var(--border)]">
                    <h3 className="font-display text-2xl uppercase">{editing ? "Edit assignment" : "Assign workout"}</h3>
                    <button onClick={onClose}><X size={18} /></button>
                </div>
                <form onSubmit={submit} className="p-5 space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="label-tiny block mb-2">Client</label>
                            <select required className="input-dark" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} data-testid="assign-client-select">
                                <option value="">Select client</option>
                                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="label-tiny block mb-2">Type</label>
                            <select className="input-dark" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                                <option value="workout">Workout</option>
                                <option value="session">1-on-1 Session</option>
                                <option value="assessment">Assessment</option>
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className="label-tiny block mb-2">Title</label>
                        <input required className="input-dark" placeholder="e.g. Upper body push" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="assign-title-input" />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                        <div>
                            <label className="label-tiny block mb-2">Date</label>
                            <input type="date" required className="input-dark" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="assign-date-input" />
                        </div>
                        <div>
                            <label className="label-tiny block mb-2">Time</label>
                            <input type="time" className="input-dark" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} />
                        </div>
                        <div>
                            <label className="label-tiny block mb-2">Duration (min)</label>
                            <input type="number" min={15} step={15} className="input-dark" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: parseInt(e.target.value) || 60 })} />
                        </div>
                    </div>
                    <div>
                        <label className="label-tiny block mb-2">Description / Instructions</label>
                        <textarea className="input-dark min-h-[80px]" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Bench 5x5, OHP 4x6, dips, triceps…" />
                    </div>
                    <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={!!form.completed} onChange={(e) => setForm({ ...form, completed: e.target.checked })} />
                        Mark as completed
                    </label>
                    {err && <div className="text-sm text-[var(--danger)]">{err}</div>}
                    <div className="flex justify-between items-center pt-2">
                        {editing?.id ? (
                            <button type="button" onClick={remove} className="text-sm text-[var(--danger)] inline-flex items-center gap-1" data-testid="delete-workout-btn">
                                <Trash2 size={14} /> Delete
                            </button>
                        ) : <span />}
                        <div className="flex gap-3">
                            <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
                            <button type="submit" className="btn-primary" disabled={busy} data-testid="save-workout-btn">
                                {busy ? "Saving…" : editing ? "Save" : "Assign"}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default function CalendarPage() {
    const [anchor, setAnchor] = useState(new Date());
    const [workouts, setWorkouts] = useState([]);
    const [clients, setClients] = useState([]);
    const [filter, setFilter] = useState(""); // client filter
    const [open, setOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [editing, setEditing] = useState(null);

    const days = useMemo(() => buildMonthGrid(anchor), [anchor]);

    const load = async () => {
        const start = ymd(days[0]);
        const end = ymd(days[days.length - 1]);
        const [w, c] = await Promise.all([
            api.get("/workouts", { params: { start, end, ...(filter ? { client_id: filter } : {}) } }),
            api.get("/clients"),
        ]);
        setWorkouts(w.data);
        setClients(c.data);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [anchor, filter]);

    // Initial filter from URL ?client=
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const c = params.get("client");
        if (c) setFilter(c);
    }, []);

    const byDay = useMemo(() => {
        const m = {};
        for (const w of workouts) (m[w.date] ||= []).push(w);
        return m;
    }, [workouts]);

    const monthLabel = anchor.toLocaleDateString("en-US", { month: "long", year: "numeric" });

    const onSaved = (item, kind) => {
        if (kind === "delete") setWorkouts(workouts.filter((w) => w.id !== item.id));
        else if (kind === "update") setWorkouts(workouts.map((w) => (w.id === item.id ? { ...w, ...item } : w)));
        else setWorkouts([...workouts, item]);
    };

    return (
        <div className="p-8 space-y-6" data-testid="calendar-page">
            <div className="flex items-end justify-between">
                <div>
                    <p className="label-tiny mb-2">/ Programming</p>
                    <h1 className="font-serif text-6xl">Calendar</h1>
                </div>
                <div className="flex items-center gap-3">
                    <select className="input-dark py-2 w-48" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="calendar-client-filter">
                        <option value="">ALL CLIENTS</option>
                        {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                    <button className="btn-primary inline-flex items-center gap-2" onClick={() => { setEditing(null); setSelectedDate(new Date()); setOpen(true); }} data-testid="new-workout-btn">
                        <Plus size={14} /> Assign
                    </button>
                </div>
            </div>

            <div className="border border-[var(--border)] bg-[var(--surface)]">
                <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border)]">
                    <h2 className="font-serif text-4xl" data-testid="calendar-month-label">{monthLabel}</h2>
                    <div className="flex items-center gap-2">
                        <button className="btn-ghost p-2" onClick={() => setAnchor(addDays(startOfMonth(anchor), -1))} data-testid="cal-prev"><ChevronLeft size={16} /></button>
                        <button className="btn-ghost px-3" onClick={() => setAnchor(new Date())} data-testid="cal-today">TODAY</button>
                        <button className="btn-ghost p-2" onClick={() => setAnchor(addDays(endOfMonth(anchor), 1))} data-testid="cal-next"><ChevronRight size={16} /></button>
                    </div>
                </div>

                {/* Weekday header */}
                <div className="grid grid-cols-7 border-b border-[var(--border)]">
                    {["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"].map((d) => (
                        <div key={d} className="px-3 py-2 label-tiny border-r border-[var(--border)] last:border-r-0">{d}</div>
                    ))}
                </div>

                {/* Days grid */}
                <div className="grid grid-cols-7">
                    {days.map((d, i) => {
                        const inMonth = d.getMonth() === anchor.getMonth();
                        const isToday = ymd(d) === ymd(new Date());
                        const items = byDay[ymd(d)] || [];
                        const dropTarget = ymd(d);
                        return (
                            <div
                                key={i}
                                onClick={() => { setEditing(null); setSelectedDate(d); setOpen(true); }}
                                onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("drop-target-on"); }}
                                onDragLeave={(e) => e.currentTarget.classList.remove("drop-target-on")}
                                onDrop={async (e) => {
                                    e.preventDefault();
                                    e.currentTarget.classList.remove("drop-target-on");
                                    const wid = e.dataTransfer.getData("workout-id");
                                    const from = e.dataTransfer.getData("from-date");
                                    if (!wid || from === dropTarget) return;
                                    try {
                                        await api.post(`/workouts/${wid}/move`, { date: dropTarget });
                                        onSaved();
                                    } catch (err) {
                                        alert("Could not move workout: " + (err.response?.data?.detail || err.message));
                                    }
                                }}
                                data-testid={`cal-day-${ymd(d)}`}
                                className={`relative cursor-pointer text-left min-h-[120px] p-2 border-r border-b border-[var(--border)] last-of-type:border-r-0 hover:bg-[var(--surface-hover)] ${
                                    inMonth ? "" : "opacity-40"
                                } ${isToday ? "bg-stripes" : ""}`}
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <span className={`font-display text-2xl ${isToday ? "text-[var(--primary)]" : ""}`}>{d.getDate()}</span>
                                    {items.length > 0 && <span className="font-mono text-[10px] text-[var(--text-muted)]">{items.length}</span>}
                                </div>
                                <div className="space-y-1">
                                    {items.slice(0, 3).map((w) => (
                                        <div
                                            key={w.id}
                                            draggable
                                            onDragStart={(e) => {
                                                e.dataTransfer.setData("workout-id", w.id);
                                                e.dataTransfer.setData("from-date", w.date);
                                                e.dataTransfer.effectAllowed = "move";
                                            }}
                                            onClick={(e) => { e.stopPropagation(); setEditing(w); setSelectedDate(d); setOpen(true); }}
                                            className="border-l-2 pl-2 py-1 text-xs truncate hover:bg-[var(--surface-2)] cursor-grab active:cursor-grabbing"
                                            style={{ borderColor: TAG_COLOR[w.client_tag] || "#FF4D00" }}
                                            data-testid={`cal-event-${w.id}`}
                                        >
                                            <span className="font-mono text-[10px] text-[var(--text-muted)]">{w.time || ""}</span>{" "}
                                            <span className={w.completed ? "line-through text-[var(--text-muted)]" : ""}>{w.client_name}</span>
                                            <div className="text-[var(--text-muted)] truncate text-[11px]">{w.title}</div>
                                        </div>
                                    ))}
                                    {items.length > 3 && <div className="text-[10px] text-[var(--text-muted)] font-mono">+{items.length - 3} more</div>}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <p className="text-[10px] font-mono text-[var(--text-muted)]">TIP: drag any workout pill to another day to reschedule it.</p>

            {/* Tag legend */}
            <div className="flex flex-wrap gap-3 text-xs font-mono text-[var(--text-muted)]">
                {Object.entries(TAG_COLOR).map(([k, v]) => (
                    <div key={k} className="inline-flex items-center gap-2">
                        <span className="inline-block w-3 h-0.5" style={{ background: v }} /> {k}
                    </div>
                ))}
            </div>

            <AssignModal open={open} onClose={() => setOpen(false)} date={selectedDate} clients={clients} onSaved={onSaved} editing={editing} />
        </div>
    );
}
